import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

interface AppSetting {
  key: string;
  value: any;
  description?: string;
}

interface RelationDeletionPrice {
  amount: number;
  currency: string;
}

// Utiliser le même type pour le prix de déverrouillage
type RelationUnlockPrice = RelationDeletionPrice;

interface ShortTermSearchUnlockPrice extends RelationUnlockPrice {
  enabled: boolean;
}

interface MobilePaymentNumber {
  number: string;
  reference: string;
}

interface MobilePaymentNumbers {
  orange_money: MobilePaymentNumber[];
  mtn_money: MobilePaymentNumber[];
}

export function useAppSettings() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [appSettings, setAppSettings] = useState<AppSetting[]>([]);
  const [realtimeConnected, setRealtimeConnected] = useState(false);

  // Fonction pour charger les paramètres depuis Supabase
  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*');
      
      if (error) {
        throw error;
      }
      
      setAppSettings(data || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      console.error('Error fetching app settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Effet pour charger les paramètres au montage du composant
  // et configurer les abonnements Realtime (avec fallback)
  useEffect(() => {
    // Charger les paramètres initiaux
    fetchSettings();

    // Configurer un intervalle de rafraîchissement de fallback
    // qui sera utilisé même si Realtime échoue
    const fallbackInterval = setInterval(() => {
      fetchSettings();
    }, 30000); // Rafraîchir toutes les 30 secondes

    let subscription: RealtimeChannel | null = null;
    try {
      // Tenter de configurer l'abonnement Realtime
      subscription = supabase
        .channel('app_settings_changes')
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'app_settings' 
          }, 
          (payload) => {
            console.log('Realtime update received:', payload);
            setRealtimeConnected(true);
            fetchSettings();
          }
        )
        .subscribe((status) => {
          console.log('Realtime subscription status:', status);
          if (status === 'SUBSCRIBED') {
            setRealtimeConnected(true);
            // Si Realtime fonctionne, on peut garder un intervalle plus long
            clearInterval(fallbackInterval);
          } else if (status === 'CHANNEL_ERROR') {
            setRealtimeConnected(false);
            // Si Realtime échoue, on garde l'intervalle pour le fallback
            console.log('Fallback to interval polling for app settings');
          }
        });
    } catch (err) {
      console.error('Error setting up Realtime subscription:', err);
      setRealtimeConnected(false);
    }

    // Nettoyer l'abonnement et l'intervalle lors du démontage du composant
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
      clearInterval(fallbackInterval);
    };
  }, []);

  // Fonction pour récupérer un paramètre spécifique par sa clé
  const getSetting = <T>(key: string, defaultValue: T): T => {
    if (!appSettings || appSettings.length === 0) {
      return defaultValue;
    }
    
    const setting = appSettings.find(s => s.key === key);
    if (!setting) {
      return defaultValue;
    }
    
    return setting.value;
  };

  // Fonction spécifique pour récupérer le prix de suppression d'une relation
  const getRelationDeletionPrice = (): RelationDeletionPrice => {
    const defaultPrice: RelationDeletionPrice = { amount: 1, currency: 'USD' };
    const price = getSetting<RelationDeletionPrice>('relation_deletion_price', defaultPrice);
    return price;
  };

  // Fonction spécifique pour récupérer le prix de déverrouillage d'une relation
  const getRelationUnlockPrice = (): RelationUnlockPrice => {
    const defaultPrice: RelationUnlockPrice = { amount: 1, currency: 'USD' };
    const price = getSetting<RelationUnlockPrice>('relation_unlock_price', defaultPrice);
    return price;
  };

  // Fonction spécifique pour récupérer le prix de déverrouillage des relations court terme dans la recherche
  const getShortTermSearchUnlockPrice = (): ShortTermSearchUnlockPrice => {
    const defaultPrice: ShortTermSearchUnlockPrice = { amount: 1, currency: 'USD', enabled: false };
    const price = getSetting<ShortTermSearchUnlockPrice>('short_term_search_unlock_price', defaultPrice);
    return price;
  };

  // Fonction pour récupérer les numéros de paiement mobile
  const getMobilePaymentNumbers = (): MobilePaymentNumbers => {
    const defaultNumbers: MobilePaymentNumbers = {
      orange_money: [{ number: "+237 6XX XXX XXX", reference: "Mycount + $userName" }],
      mtn_money: [{ number: "+237 6YY YYY YYY", reference: "Mycount + $userName" }]
    };
    const numbers = getSetting<MobilePaymentNumbers>('mobile_payment_numbers', defaultNumbers);
    return numbers;
  };

  // Fonction pour formater le prix selon la devise
  const formatPrice = (price: RelationDeletionPrice): string => {
    const { amount, currency } = price;
    
    // Formater selon la devise
    let formattedPrice = '';
    switch (currency) {
      case 'USD':
        formattedPrice = `${amount}$`;
        break;
      case 'EUR':
        formattedPrice = `${amount}€`;
        break;
      case 'GBP':
        formattedPrice = `£${amount}`;
        break;
      case 'XAF':
        formattedPrice = `${amount} FCFA`;
        break;
      default:
        formattedPrice = `${amount} ${currency}`;
    }
    
    return formattedPrice;
  };

  // Fonction pour récupérer le taux de commission sur les parrainages
  const getReferralCommissionRate = (): number => {
    const defaultRate = 10; // 10% par défaut
    const rate = getSetting<number>('referral_commission_rate', defaultRate);
    return rate;
  };

  return {
    isLoading,
    error,
    realtimeConnected,
    getSetting,
    getRelationDeletionPrice,
    getRelationUnlockPrice,
    getShortTermSearchUnlockPrice,
    getMobilePaymentNumbers,
    formatPrice,
    refreshSettings: fetchSettings,
    getReferralCommissionRate
  };
} 